import React, { useState } from "react";
import { Modal, Skeleton, Table } from "antd";
import { CALL_FEATCH_API } from "common/API";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useMutate } from "hooks/useMutate";
import moment from "moment";
import styled from "styled-components";

// ─── Styled Table ─────────────────────────────────────────────────────────────

const StyledTable = styled(Table)`
  .ant-table-cell {
    padding: 10px 14px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const UnapprovedTimesheetModal = ({ matterId, visible, onClose }) => {
  const [unapprovedData, setUnapprovedData]   = useState([]);
  const [isLoading,      setIsLoading]        = useState(false);

  // ── Fetch engagement data to get employeeList ──────────────────────────────
  const { getMatterById } =
    queryKeys;

  const { key: engKey, url: engUrl } = getMatterById(matterId);

  const { isLoading: engLoading } = useFetch(engKey, engUrl, {
    enabled: !!matterId && visible,
    onSuccess: (res) => {
      // Once engagement data is loaded, auto-fetch unapproved timesheets
      const engData = res?.response?.data;
      if (engData) fetchUnapprovedTimesheets(engData);
    },
  });

  const { key: unApprovedKey, url: unapprovedUrl } =
    queryKeys.unapprovedTimesheets;
  const { mutate: unapprovedTimesheets } = useMutate(
    unApprovedKey,
    unapprovedUrl
  );

  const fetchUnapprovedTimesheets = (engagementData) => {
    const uniqueUserIds = [
      ...new Set(
        (engagementData?.employeeList || [])
          ?.map((item) => item.userId)
          ?.filter((id) => id !== null && id !== "")
      ),
    ];

    const payload = {
      resourceList: uniqueUserIds,
      matterId: matterId,
    };
    setIsLoading(true);
    unapprovedTimesheets(payload, {
      onSuccess: (res) => {
        setUnapprovedData(
          res?.response?.response || []
        );
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });
  };

  // ── Date formatter ─────────────────────────────────────────────────────────
  const formatWeekCell = (text) => {
    if (!text) return "-";
    const [start, end] = text.split("-");
    const formatPart = (dateStr) => {
      if (!dateStr || dateStr.length < 8) return dateStr;
      const year  = dateStr.slice(0, 4);
      const month = dateStr.slice(4, 7);
      const day   = dateStr.slice(7);
      return `${day} ${month} ${year}`;
    };
    return `${formatPart(start)} - ${formatPart(end)}`;
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: "Week Ending",
      dataIndex: "week",
      key: "week",
      render: (text) => formatWeekCell(text),
    },
    {
      title: "Task",
      key: "task",
      render: (_, record) => {
        const tasks = record?.timeSheet
          ?.filter((item) => item?.engagement === matterId)
          ?.map((item) => item?.task)
          ?.filter(Boolean);
        return tasks?.length > 0 ? tasks.join(" - ") : "--";
      },
    },
    {
      title: "Hours",
      key: "sum",
      render: (_, record) =>
        record?.timeSheet
          ?.filter((item) => item?.engagement === matterId)
          ?.reduce((acc, item) => acc + (item?.sum || 0), 0) || 0,
    },
    {
      title: "Remarks",
      key: "remarks",
      render: (_, record) => {
        const remarks = record?.timeSheet
          ?.filter((item) => item?.engagement === matterId)
          ?.map((item) => item?.remarks)
          ?.filter(Boolean);
        return remarks?.length > 0 ? remarks.join(" - ") : "--";
      },
    },
    {
      title: "Claim Amount",
      key: "claimAmount",
      render: (_, record) =>
        record?.timeSheet
          ?.filter((item) => item?.engagement === matterId)
          ?.flatMap((item) => item?.claims || [])
          ?.reduce((sum, claim) => sum + parseFloat(claim?.amount || 0), 0),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      title="Unapproved Timesheets"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
      centered
    >
      {(engLoading || isLoading) ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <StyledTable
          dataSource={unapprovedData}
          rowKey={(record) => record?.timeSheetId}
          columns={columns}
          pagination={false}
        />
      )}
    </Modal>
  );
};

export default UnapprovedTimesheetModal;